import { clsx } from "clsx";
import { forwardRef, useRef } from "react";
import { FocusScope, mergeProps, OverlayContainer, useDialog, usePreventScroll } from "react-aria";

import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import { useDOMRef } from "../../hooks/useDomRef.ts";
import { useScroll } from "../../hooks/useScroll.ts";
import type { AllDialogProps, DialogProps, PopoverProps } from "../../types/dialogs.d.ts";
import { HeaderBar, type HeaderBarProps } from "../HeaderBar/HeaderBar.tsx";
import { DialogButtonsWithErrorCallout } from "./DialogButtonsWithErrorCallout.tsx";
import style from "./TabDialog.module.css";
import { useDialogHeaderActionsWithClose } from "./useDialogHeaderActionsWithClose.tsx";

export type DialogComponentProps = AllDialogProps & {
  domTitleProps?: HeaderBarProps["domTitleProps"];
  placement?: string;
  titleTruncateMiddle?: boolean;
};

export const DialogComponent = forwardRef<HTMLDivElement, DialogComponentProps>(function DialogComponent(
  props: DialogComponentProps,
  ref,
) {
  const dialogRef = useDOMRef(ref);
  const { dialogProps, titleProps } = useDialog({ role: "dialog", ...props }, dialogRef);

  const actions = useDialogHeaderActionsWithClose(props);

  const scrollRef = useRef<HTMLDivElement>(null);
  const scroll = useScroll(scrollRef);

  const dataAttributes = useDataAttributes(props);
  const { style: _, ...rest } = props;

  return (
    <div
      ref={dialogRef}
      {...mergeProps(dialogProps, props.domProps)}
      className={clsx(
        style.dialog,
        {
          [style.centered]: !!(props as DialogProps).underlayProps,
          [style.fullscreen]: props.fullscreen,
        },
        props.className,
      )}
      style={{ ...props.style, ...props.domProps?.style, width: props.width }}
      {...dataAttributes}
      data-testid={`${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}dialog`}
    >
      <div className={style.rounded_container}>
        <HeaderBar
          {...rest}
          data-testid={undefined}
          domTitleProps={titleProps}
          actions={actions}
          className={clsx(style.header_bar, { [style.header_bar_scroll]: scroll })}
          titleTruncateMiddle={props.titleTruncateMiddle}
        />
        <div
          style={props.contentStyle}
          className={clsx(style.content, props.contentClassName)}
          ref={scrollRef}
          data-testid="content"
        >
          {props.children}
        </div>
        {props.mainButton != undefined && (
          <DialogButtonsWithErrorCallout {...rest} className={clsx(props.className, style.footer_bar)} />
        )}
      </div>
      {(props as PopoverProps).arrowProps != undefined && (
        <div className={style.arrow} data-placement={props.placement} {...(rest as PopoverProps).arrowProps} />
      )}
    </div>
  );
});

/** @deprecated use DialogComponent with Popover or Modal */
export const Dialog = forwardRef<HTMLDivElement, AllDialogProps>(function Dialog(props, ref) {
  usePreventScroll();

  return (
    <OverlayContainer
      {...((props as DialogProps).underlayProps ?? {})}
      className={(props as DialogProps).underlayProps ? style.overlay : undefined}
    >
      <FocusScope contain={!props.noContainedFocus} restoreFocus={!props.noRestoreFocus}>
        <DialogComponent {...props} domProps={props.domProps} ref={ref} />
      </FocusScope>
    </OverlayContainer>
  );
});
